import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AirRoute } from '../../types';

interface ProcessDetailData {
  corePrinciple: string;
  keyPoints: string[];
  technicalPoints: string[];
  comparison?: {
    title: string;
    headers: string[];
    rows: string[][];
  };
  notes?: string[];
}

const processDetailsMap: Record<string, ProcessDetailData> = {
  'cryogenic-oxygen': {
    corePrinciple: '深冷法制氧基于空气中各组分沸点差异进行低温精馏分离\n氮气沸点-195.8°C，氩气沸点-186.0°C，氧气沸点-183.0°C\n通过双塔精馏系统实现高效分离，氧回收率>95%',
    keyPoints: [
      '双塔精馏系统：高压塔(下塔)5-6 bar，低压塔(上塔)1.2-1.3 bar',
      '林德双塔精馏：1910年发明，氧回收率>95%，所有深冷空分装置的标准架构',
      '法液空克劳德工艺：1902年发明，涡轮膨胀机制冷，制冷效率大幅提高',
      '现代装置集成：克劳德循环提供低温制冷，林德双塔进行分离精馏',
      '连续运行周期：超过2年无需大修，设备可靠性极高',
    ],
    technicalPoints: [
      '氧纯度：99.5%-99.999%',
      '氧回收率：>95%',
      '单位能耗：0.3-0.6 kWh/Nm³ O₂',
      '单套最大：120,000 Nm³/h',
      '运行周期：>2年',
    ],
    notes: [
      '大型工业供气首选技术',
      '启动时间长(数小时)，不适合频繁启停',
      '设备投资大，适合大规模稳定需求',
      '全球市场份额最大，技术最成熟',
    ],
  },
  'psa-oxygen': {
    corePrinciple: '变压吸附法利用沸石分子筛优先吸附氮气的特性\n通过压力变化实现氧氮分离：高压吸附、低压脱附\n双床交替运行确保连续生产',
    keyPoints: [
      'PSA工艺：高压吸附、低压脱附，氧纯度90%-95%',
      'VPSA工艺：脱附采用真空条件，氧纯度93%-97%',
      '吸附剂：沸石分子筛，优先吸附氮气',
      '双床交替：确保连续生产，切换时间短',
      '启动时间：约30分钟，适合间歇运行',
    ],
    technicalPoints: [
      'PSA纯度：90%-95%',
      'VPSA纯度：93%-97%',
      '启动时间：~30分钟',
      '规模：<500 Nm³/h',
      '投资成本：低',
    ],
    notes: [
      '适合中小规模现场供氧',
      '氧纯度有限，无法达到深冷法水平',
      '能耗较高，不适合大规模生产',
      '投资低，操作简单，维护方便',
    ],
  },
  'membrane-oxygen': {
    corePrinciple: '膜分离法利用气体在高分子膜中的渗透速率差异\n氧气(快气)渗透快于氮气(慢气)\n获得富氧空气(30%-45%)，无法获得纯氧',
    keyPoints: [
      '膜材料：中空纤维膜(聚砜、聚酰亚胺等)',
      '氧气渗透快，氮气渗透慢',
      '几乎无运动部件，极低能耗',
      '模块化部署，可户外安装',
      '启动时间<5分钟，响应极快',
    ],
    technicalPoints: [
      '氧纯度：30%-45%',
      '启动时间：<5分钟',
      '能耗：极低',
      '维护：极低',
      '膜材料：聚砜/聚酰亚胺',
    ],
    notes: [
      '仅能获得富氧空气，无法获得纯氧',
      '适合医疗富氧、高原供氧、生命保障',
      '膜寿命有限，需定期更换',
      '模块化设计，可快速部署',
    ],
  },
  'cryogenic-nitrogen': {
    corePrinciple: '深冷法制氮与氧气在同一套双塔精馏系统中联产\n上塔顶部产出高纯氮气(最高99.999%)\n大规模联产摊薄成本，能耗最低',
    keyPoints: [
      '与氧气联产：同一套双塔精馏系统',
      '上塔顶部提氮：最高纯度99.999%',
      '大规模联产：成本最低',
      '能耗低：联产摊薄',
      '适合大规模工业需求',
    ],
    technicalPoints: [
      '氮纯度：最高99.999%',
      '上塔压力：1.2-1.3 bar',
      '氮沸点：-195.8°C',
      '联产：O₂+N₂',
      '成本：大规模最低',
    ],
    notes: [
      '大规模工业联产首选',
      '氮纯度极高，可达99.999%',
      '启动时间长，不适合频繁启停',
      '设备投资大，适合稳定大规模需求',
    ],
  },
  'psa-nitrogen': {
    corePrinciple: '变压吸附法制氮利用碳分子筛(CMS)优先吸附氧气\n氮气留在吸附床成为产品\n氮气纯度97%-99.999%，适合现场制氮',
    keyPoints: [
      '吸附剂：碳分子筛(CMS)，优先吸附氧气',
      '氮气纯度：97%-99.999%',
      '产能：1-3000 Nm³/h',
      '启动时间：约30分钟',
      '投资低，操作简单',
    ],
    technicalPoints: [
      '氮纯度：97%-99.999%',
      '产能：1-3000 Nm³/h',
      '启动时间：~30分钟',
      '吸附剂：碳分子筛(CMS)',
      '投资：低',
    ],
    notes: [
      '适合中小规模现场制氮',
      '投资低，操作简单',
      '能耗中等',
      '规模有限，不适合大规模需求',
    ],
  },
  'membrane-nitrogen': {
    corePrinciple: '膜分离法制氮：氧气(快气)优先渗透\n氮气(慢气)留在非渗透侧成为产品\n纯度95%-99.9%，能耗最低',
    keyPoints: [
      '氧气渗透快，氮气截留',
      '氮气纯度：95%-99.9%',
      '无运动部件，极低维护',
      '模块化，可户外安装',
      '能耗最低',
    ],
    technicalPoints: [
      '氮纯度：95%-99.9%',
      '运动部件：无',
      '维护：极低',
      '部署：模块化',
      '能耗：最低',
    ],
    notes: [
      '适合低纯度氮气需求',
      '油田注氮、惰化保护、食品保鲜',
      '无运动部件，维护极低',
      '模块化设计，可快速部署',
    ],
  },
  'cryogenic-argon': {
    corePrinciple: '深冷法提取氩气：从空分装置上塔中部抽取氩馏分\n氩气在空气中含量约0.93%，沸点-186.0°C\n经粗氩塔和精氩塔精馏获得纯氩≥99.999%',
    keyPoints: [
      '氩馏分：含Ar 8%-15%，从上塔中部抽取',
      '粗氩塔：精馏至Ar 95%-98%',
      '精氩塔：脱氮至纯氩≥99.999%',
      '全精馏制氩：O₂<1 ppm，无需加氢',
      '氩回收率：70%-85%',
    ],
    technicalPoints: [
      '氩回收率：70%-85%',
      '纯度：≥99.999%(5N)',
      '电子级：可达6N',
      '全精馏：O₂<1 ppm',
      '氩沸点：-186.0°C',
    ],
    notes: [
      '全精馏制氩更安全高效',
      '无需加氢除氧，降低成本',
      '电子级氩气可达6N',
      '依赖空分装置，投资大',
    ],
  },
  'helium-extraction': {
    corePrinciple: '天然气提氦：从含氦天然气(He 0.3%-7%)中深冷分离\n空气中氦含量仅~5 ppm，经济上不可行\n天然气提氦是唯一经济可行的路线',
    keyPoints: [
      '天然气氦含量：0.3%-7%',
      '粗氦提取：深冷分离至He 50%-70%',
      '粗氦纯化：PSA/TSA+膜分离',
      '纯氦：≥99.999%(5N)',
      '液化：4.2K(-268.9°C)',
    ],
    technicalPoints: [
      '天然气氦含量：0.3%-7%',
      '纯度：≥99.999%',
      '液化温度：4.2K',
      '中国氦资源：<2%',
      '依赖进口：>90%',
    ],
    notes: [
      '中国氦资源极度匮乏，90%以上依赖进口',
      '美国、卡塔尔、阿尔及利亚是主要氦气生产国',
      '战略资源，对国防、航天、医疗至关重要',
      '"绿色氦"新方向：富氮储层提氦，CO₂排放降低99%',
    ],
  },
  'smr-hydrogen': {
    corePrinciple: '天然气蒸汽转化制氢：CH₄ + H₂O → CO + 3H₂\n一次转化温度700-850℃，Ni催化剂\n水气变换：CO + H₂O → CO₂ + H₂\nPSA纯化至97%-99.999%',
    keyPoints: [
      '一次转化：700-850℃，Ni催化剂',
      '水气变换：提高氢气收率',
      'PSA纯化：氢气纯度97%-99.999%',
      '制氢成本：1.0-2.0美元/kg',
      '全球占比：约48%',
    ],
    technicalPoints: [
      '制氢成本：1.0-2.0美元/kg',
      '碳排放：~9.4 kg CO₂/kg H₂',
      '转化温度：700-850℃',
      '催化剂：Ni',
      '全球占比：48%',
    ],
    notes: [
      '全球主流制氢路线，技术最成熟',
      '制氢成本最低',
      '碳排放较高，需配套CCS实现蓝氢',
      '依赖天然气资源',
    ],
  },
  'coal-gasification-hydrogen': {
    corePrinciple: '煤气化制氢：C + H₂O → CO + H₂\n气化温度1200-1400℃，生成合成气\n水气变换和PSA纯化制氢\n中国占比约62%',
    keyPoints: [
      '气化温度：1200-1400℃',
      '合成气净化：脱除杂质',
      '水气变换：提高氢气收率',
      'PSA纯化：氢气纯度>99.5%',
      '中国占比：约62%',
    ],
    technicalPoints: [
      '制氢成本：1.0-1.5美元/kg',
      '碳排放：~24 kg CO₂/kg H₂',
      '气化温度：1200-1400℃',
      '中国占比：62%',
      '资源：富煤',
    ],
    notes: [
      '中国富煤资源禀赋决定的主导路线',
      '碳排放最高，环境污染严重',
      '需配套CCS实现低碳制氢',
      '成本较低，技术成熟',
    ],
  },
  'electrolysis-hydrogen': {
    corePrinciple: '电解水制氢：2H₂O → 2H₂ + O₂\n利用电能分解水，可实现零碳排放\n三种技术：碱性电解(AEC)、PEM电解、SOEC高温电解',
    keyPoints: [
      'AEC碱性电解：最成熟，电耗4.5-5.5 kWh/Nm³',
      'PEM电解：启动快，电耗3.8-5.0 kWh/Nm³',
      'SOEC高温电解：电耗最低2.5-3.5 kWh/Nm³',
      '零碳排放：绿氢，与可再生能源耦合',
      '可分布式生产',
    ],
    technicalPoints: [
      'AEC电耗：4.5-5.5 kWh/Nm³ H₂',
      'PEM电耗：3.8-5.0 kWh/Nm³ H₂',
      'SOEC电耗：2.5-3.5 kWh/Nm³ H₂',
      'AEC成熟度：★★★★★',
      '碳排放：~0(可再生电力)',
    ],
    notes: [
      '绿氢路线，零碳排放',
      '成本高(4.0-7.0美元/kg)，电耗大',
      'AEC技术最成熟，PEM/SOEC待发展',
      '与可再生能源耦合，实现可持续发展',
    ],
  },
};

interface AirProcessDetailProps {
  route: AirRoute;
  onClose: () => void;
}

const AirProcessDetail: React.FC<AirProcessDetailProps> = ({ route, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const detailData = processDetailsMap[route.id];

  if (!detailData) {
    return (
      <Card sx={{ height: '100%', backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff' }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary">
            暂无该工艺的详细信息
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: isDarkMode ? '#1e1e1e' : '#ffffff',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h5"
            sx={{ color: isDarkMode ? '#66bb6a' : '#2e7d32', fontWeight: 600 }}
          >
            {route.name} - 工艺详情
          </Typography>
          <Tooltip title="关闭详情">
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Divider sx={{ mb: 2, borderColor: isDarkMode ? '#444444' : '#e0e0e0' }} />

        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? '#ffb74d' : '#ff9800',
              mb: 1,
            }}
          >
            核心原理
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              backgroundColor: isDarkMode ? '#2a2a2a' : '#f5f5f5',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.8 }}
            >
              {detailData.corePrinciple}
            </Typography>
          </Paper>
        </Box>

        {detailData.technicalPoints && detailData.technicalPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#81d4fa' : '#0288d1',
                mb: 1,
              }}
            >
              技术参数
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {detailData.technicalPoints.map((point, index) => (
                <Chip
                  key={index}
                  label={point}
                  size="small"
                  sx={{
                    backgroundColor: isDarkMode ? '#0d47a1' : '#e3f2fd',
                    color: isDarkMode ? '#90caf9' : '#1976d2',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {detailData.keyPoints && detailData.keyPoints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ce93d8' : '#9c27b0',
                mb: 1,
              }}
            >
              关键技术点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.keyPoints.map((point, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: isDarkMode ? '#4a148c' : '#f3e5f5',
                    borderRadius: 1,
                    borderLeft: '4px solid',
                    borderColor: isDarkMode ? '#ce93d8' : '#9c27b0',
                  }}
                >
                  <Typography variant="body2">{point}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {detailData.comparison && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#66bb6a' : '#2e7d32',
                mb: 1,
              }}
            >
              {detailData.comparison.title}
            </Typography>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                backgroundColor: isDarkMode ? '#2a2a2a' : '#fafafa',
                border: '1px solid',
                borderColor: isDarkMode ? '#444444' : '#e0e0e0',
              }}
            >
              <Table size="small">
                <TableBody>
                  <TableRow sx={{ backgroundColor: isDarkMode ? '#1b5e20' : '#e8f5e9' }}>
                    {detailData.comparison.headers.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? '#66bb6a' : '#2e7d32',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                  {detailData.comparison.rows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <TableCell key={cellIndex}>{cell}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {detailData.notes && detailData.notes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: isDarkMode ? '#ffcc80' : '#ff9800',
                mb: 1,
              }}
            >
              实践要点
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {detailData.notes.map((note, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{
                    pl: 2,
                    borderLeft: '3px solid',
                    borderColor: isDarkMode ? '#ffcc80' : '#ff9800',
                  }}
                >
                  {note}
                </Typography>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AirProcessDetail;
